import React, { useEffect, useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";

import { Text, toast } from "@performyard/py-components";

import RoleForm from "./RoleForm";

import { useCompetencies } from "../../../../api/hooks/defs/competencies";
import { useRole } from "../../../../api/hooks/defs/roles";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import { EMPTY_FORM_VALUES, ROLES_LIST_PATH, type RoleFormValues, fromRole } from "./validationSchema";

export interface CreateEditRoleProps {
	mode: "create" | "edit";
}

const CreateEditRole: React.FC<CreateEditRoleProps> = ({ mode }) => {
	const { roleId = "" } = useParams<{ roleId: string }>();
	const isEdit = mode === "edit";

	const { value: competenciesEnabled, query: competencyFlagQuery } = useFeatureFlagQuery(
		"ff-is-competency-management-enabled",
		false
	);

	const roleQuery = useRole(isEdit ? roleId : "");
	// Active company-wide competencies seed the create form so admins can set
	// expected levels on submit; in edit mode the API's merge-on-read already
	// includes them, so this query stays disabled. Gated on the FF — /v1/competencies
	// is feature-flagged, so an FF-off create would 403 without this.
	const cwCompetenciesQuery = useCompetencies(isEdit ? {} : { status: "active", companyWide: true, limit: 500 }, {
		enabled: !isEdit && competenciesEnabled,
	});

	// Gate create-mode render on the flag query too: until it resolves it returns the `false`
	// fallback, which would render the form with competencies disabled, then remount (losing input)
	// when the flag flips true and the company-wide query starts.
	const isLoading =
		(isEdit && roleQuery.isLoading) ||
		(!isEdit && (competencyFlagQuery.isLoading || cwCompetenciesQuery.isLoading));
	const loadError = isEdit && roleQuery.error;
	const isArchived = isEdit && roleQuery.data?.role?.status === "archived";

	useEffect(() => {
		if (loadError) toast.error("Role not found");
		else if (isArchived) toast.error("Reopen this role to edit it");
	}, [loadError, isArchived]);

	const initialValues: RoleFormValues = useMemo(() => {
		if (isEdit && roleQuery.data?.role) {
			return fromRole(roleQuery.data.role);
		}
		const seeded = (cwCompetenciesQuery.data?.competencies ?? []).map((c) => ({
			competency_id: c._id,
			expected_level: null,
		}));
		return { ...EMPTY_FORM_VALUES, competencies: seeded };
	}, [isEdit, roleQuery.data, cwCompetenciesQuery.data]);

	if (loadError || isArchived) return <Navigate to={ROLES_LIST_PATH} replace />;

	if (isLoading) {
		return <div data-testid="role-form-loading" />;
	}

	const title = isEdit ? "Edit Role" : "Create a Role";
	const subtitle = isEdit ? "Edit role details and competencies" : "Fill out this form to create a role";

	return (
		<div className="flex min-h-full justify-center bg-neutral-1 px-6 py-8">
			<div className="w-full max-w-[560px] self-start rounded-md bg-white p-8 shadow-sm">
				<div className="mb-6 flex flex-col items-center gap-1">
					<Text size="sub-heading" weight="semi-bold" color="neutral-9">
						{title}
					</Text>
					<Text size="paragraph-2" weight="regular" color="neutral-7">
						{subtitle}
					</Text>
				</div>

				<RoleForm mode={mode} roleId={isEdit ? roleId : undefined} initialValues={initialValues} />
			</div>
		</div>
	);
};

export default CreateEditRole;
